import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Calendar, Users, Package } from "lucide-react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PACKAGES } from "@/types/booking";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { z } from "zod";

const bookingSchema = z.object({
  name: z.string().trim().min(2, "Name must be at least 2 characters").max(100, "Name must be less than 100 characters"),
  email: z.string().trim().email("Invalid email address").max(255, "Email must be less than 255 characters"),
  phone: z.string().trim().regex(/^\+?[1-9]\d{1,14}$/, "Invalid phone number format (e.g., +971XXXXXXXXX)")
});

const Payment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const bookingData = location.state;

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    if (!bookingData) {
      navigate("/");
      return;
    }

    // Check authentication
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error("Please login to complete your booking");
        navigate("/auth");
        return;
      }
      setUserId(session.user.id);
    };
    checkAuth();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!session) {
        navigate("/auth");
      } else {
        setUserId(session.user.id);
      }
    });

    return () => subscription.unsubscribe();
  }, [bookingData, navigate]);

  if (!bookingData) {
    return null;
  }

  const selectedPackage = PACKAGES.find(
    (pkg) => pkg.id === bookingData.packageId
  );

  if (!selectedPackage) {
    return null;
  }

  const totalAmount = selectedPackage.price * bookingData.guests;
  const bookingDate = new Date(bookingData.date);

  const handlePayment = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!userId) {
      toast.error("Please login to complete your booking");
      navigate("/auth");
      return;
    }

    // Validate guest count
    if (bookingData.guests < 1 || bookingData.guests > 20) {
      toast.error("Guest count must be between 1 and 20");
      return;
    }

    // Validate input data
    const validationResult = bookingSchema.safeParse({ name, email, phone });
    if (!validationResult.success) {
      const error = validationResult.error.errors[0];
      toast.error(error.message);
      return;
    }

    try {
      // Save booking to database with user_id
      const { error } = await supabase.from("bookings").insert({
        user_id: userId,
        guest_name: validationResult.data.name,
        guest_email: validationResult.data.email,
        guest_phone: validationResult.data.phone,
        package_id: bookingData.packageId,
        package_name: selectedPackage.name,
        travel_date: format(bookingDate, "yyyy-MM-dd"),
        number_of_guests: bookingData.guests,
        total_amount: totalAmount,
      });

      if (error) throw error;

      toast.success("Booking saved successfully!", {
        description: `Thank you ${validationResult.data.name}! Your booking has been recorded.`,
      });

      // Clear form
      setName("");
      setEmail("");
      setPhone("");
    } catch (error: any) {
      if (error?.message) {
        toast.error(`Failed to save booking: ${error.message}`);
      } else {
        toast.error("Failed to save booking. Please try again.");
      }
    }
  };

  return (
    <div className="min-h-screen bg-secondary/20">
      {/* Header */}
      <header className="bg-primary text-primary-foreground py-4 px-4 shadow-md">
        <div className="container mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate("/")}
            className="text-primary-foreground hover:bg-primary-foreground/10"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Button>
        </div>
      </header>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-4xl font-bold mb-8 text-center">
            Complete Your Booking
          </h1>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Booking Summary */}
            <Card>
              <CardHeader>
                <CardTitle>Booking Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-start gap-3">
                  <Package className="h-5 w-5 text-primary mt-1" />
                  <div>
                    <p className="font-semibold">{selectedPackage.name}</p>
                    <p className="text-sm text-muted-foreground">
                      AED {selectedPackage.price} per person
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Users className="h-5 w-5 text-primary mt-1" />
                  <div>
                    <p className="font-semibold">
                      {bookingData.guests} {bookingData.guests === 1 ? "Guest" : "Guests"}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      AED {selectedPackage.price} × {bookingData.guests}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Calendar className="h-5 w-5 text-primary mt-1" />
                  <div>
                    <p className="font-semibold">
                      {format(bookingDate, "EEEE, MMMM d, yyyy")}
                    </p>
                    <p className="text-sm text-muted-foreground">Travel Date</p>
                  </div>
                </div>

                <div className="border-t pt-4 mt-6">
                  <div className="flex justify-between items-center text-xl font-bold">
                    <span>Total Amount</span>
                    <span className="text-primary">AED {totalAmount}</span>
                  </div>
                </div>

                <div className="bg-secondary/50 p-4 rounded-lg mt-4">
                  <h3 className="font-semibold mb-2">Package Includes:</h3>
                  <ul className="space-y-1 text-sm text-muted-foreground">
                    {selectedPackage.features.map((feature, index) => (
                      <li key={index}>• {feature}</li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Payment Form */}
            <Card>
              <CardHeader>
                <CardTitle>Guest Information</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handlePayment} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Enter your full name"
                      required
                      className="h-12"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your.email@example.com"
                      required
                      className="h-12"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="+971 XX XXX XXXX"
                      required
                      className="h-12"
                    />
                  </div>

                  <div className="pt-6">
                    <Button
                      type="submit"
                      className="w-full h-14 text-lg font-semibold"
                      size="lg"
                    >
                      Pay AED {totalAmount}
                    </Button>
                    <p className="text-xs text-muted-foreground text-center mt-3">
                      Stripe payment integration will be added next
                    </p>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Payment;
